// Formáty podle docs/04: datum `9. 9. 2026`, čas `08:12`, datum a čas `9. 9. 2026 08:12`.
// Vstup je ISO (`2026-09-09` nebo `2026-09-09T06:12:00Z`); neplatný vstup → „—“.

function datumZIso(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Kalendář a hodiny aplikace jsou pražské (1. 11. 2026) — stejně jako na serveru, ať je prohlížeč kdekoli. */
const PRAHA_DEN = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Prague',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const PRAHA_CAS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Prague',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Den `RRRR-MM-DD` podle pražského kalendáře. Date přesně o místní půlnoci (datum poskládané v prohlížeči,
 * `new Date(r, m, d)`) je kalendářní den a čte se z místních složek; jakýkoli jiný okamžik podle Prahy —
 * náhrada za `d.toISOString().slice(0, 10)`, která dává den v UTC (po půlnoci pražského času předchozí).
 */
export function denPraha(d: Date): string {
  if (
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0
  ) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  const casti = PRAHA_DEN.formatToParts(d);
  const cast = (typ: Intl.DateTimeFormatPartTypes) =>
    casti.find((c) => c.type === typ)?.value ?? '';
  return `${cast('year')}-${cast('month')}-${cast('day')}`;
}

/** Dnešek `RRRR-MM-DD` v Praze. */
export function dnesPraha(ted: Date = new Date()): string {
  return denPraha(ted);
}

/** ISO bez zóny (`2026-09-15T10:22:00`) je pražský „čas na stěně“ — ukáže se doslova, bez přepočtu. */
const BEZ_ZONY = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

export function formatDatum(iso: string | undefined | null): string {
  if (!iso) return '—';
  // Samotné datum a čas bez zóny se čtou doslova, okamžik (se `Z` nebo posunem) podle pražského kalendáře.
  const bezZony = BEZ_ZONY.exec(iso);
  const den = bezZony
    ? (bezZony[1] ?? null)
    : /^\d{4}-\d{2}-\d{2}$/.test(iso)
      ? iso
      : (() => {
          const d = datumZIso(iso);
          return d ? denPraha(d) : null;
        })();
  if (!den) return '—';
  const [r, m, dd] = den.split('-');
  return `${Number(dd)}. ${Number(m)}. ${r}`;
}

export function formatCas(iso: string | undefined | null): string {
  const d = datumZIso(iso);
  if (!d) return '—';
  // Samotné datum nemá čas (půlnoc); čas bez zóny doslova; okamžik v pražském čase.
  if (iso && iso.length === 10) return '00:00';
  const bezZony = iso ? BEZ_ZONY.exec(iso) : null;
  if (bezZony) return `${bezZony[2]}:${bezZony[3]}`;
  return PRAHA_CAS.format(d);
}

export function formatDatumCas(iso: string | undefined | null): string {
  const d = datumZIso(iso);
  return d ? `${formatDatum(iso)} ${formatCas(iso)}` : '—';
}

/**
 * Částka podle docs/04: `1 950 Kč`, desetiny jen když jsou (`12,50 Kč`); neplatná → „—“.
 * Mezi skupinami běžná mezera — buňky s cenou drží na jednom řádku CSS (`white-space: nowrap`).
 */
export function formatKc(castka: number | undefined | null): string {
  return formatCastku(castka, 'CZK');
}

/**
 * Částka **v měně dokladu**: stejný formát jako `formatKc`, jen s kódem měny místo „Kč“
 * (`1 950 EUR`). Doklad v eurech se ukazuje v eurech — psát „Kč“ k eurové částce by lhalo.
 * Přepočet na koruny dělá server a ukazuje se u kurzu, ne tady.
 */
export function formatCastku(
  castka: number | undefined | null,
  mena: string | undefined | null,
): string {
  if (castka === undefined || castka === null || !Number.isFinite(castka)) return '—';
  const [cele = '0', desetiny = '00'] = Math.abs(castka).toFixed(2).split('.');
  const skupiny = cele.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const jednotka = !mena || mena === 'CZK' ? 'Kč' : mena;
  return `${castka < 0 ? '−' : ''}${skupiny}${desetiny === '00' ? '' : `,${desetiny}`} ${jednotka}`;
}

/**
 * Kurz v Kč za jednu jednotku měny česky: aspoň dvě desetinná místa, až šest (měny s kurzem za 100 jednotek —
 * JPY, HUF — mají za jednu jednotku `0,13451`). Neplatný kurz → „—“.
 */
export function formatKurz(kurz: number | undefined | null): string {
  if (kurz === undefined || kurz === null || !Number.isFinite(kurz)) return '—';
  return kurz.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

/**
 * Jednotková cena, která může mít víc než haléře: až 4 desetinná místa bez nul navíc, nejméně dvě
 * (`0,045 EUR`, `1,0964 Kč`, `12,50 Kč`). Pro ceny za MJ — částky dokladů a součty zůstávají na haléře.
 */
export function formatCenuPresne(
  castka: number | undefined | null,
  mena: string | undefined | null = 'CZK',
): string {
  if (castka === undefined || castka === null || !Number.isFinite(castka)) return '—';
  const [cele = '0', desetiny = '0000'] = Math.abs(castka).toFixed(4).split('.');
  const skupiny = cele.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const orezane = desetiny.replace(/0+$/, '').padEnd(2, '0');
  const jednotka = !mena || mena === 'CZK' ? 'Kč' : mena;
  return `${castka < 0 ? '−' : ''}${skupiny}${orezane === '00' ? '' : `,${orezane}`} ${jednotka}`;
}

/**
 * Množství s jednotkou podle docs/04: `1 240 kg`, `2,5 t`, `3 ks`; desetiny jen když jsou (nejvýš 3 místa);
 * neplatné → „—“. Mezi skupinami běžná mezera — buňky drží na jednom řádku CSS (`white-space: nowrap`).
 */
export function formatMnozstvi(mnozstvi: number | undefined | null, mj?: string | null): string {
  if (mnozstvi === undefined || mnozstvi === null || !Number.isFinite(mnozstvi)) return '—';
  const zaokrouhlene = Math.round(mnozstvi * 1000) / 1000;
  const [cele = '0', desetiny = ''] = Math.abs(zaokrouhlene).toString().split('.');
  const skupiny = cele.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const cislo = `${zaokrouhlene < 0 ? '−' : ''}${skupiny}${desetiny ? `,${desetiny}` : ''}`;
  return mj ? `${cislo} ${mj}` : cislo;
}

/** Počet s českým tvarem („1 občan“, „3 občané“, „5 občanů“) — čísla ze serveru, UI jen skloňuje. */
export function pocet(n: number, jeden: string, dva: string, pet: string): string {
  const slovo = n === 1 ? jeden : n >= 2 && n <= 4 ? dva : pet;
  return `${n.toLocaleString('cs-CZ')} ${slovo}`;
}
